import React, { useState, useEffect, useCallback } from 'react';
import {
  Inbox,
  Folder,
  FolderPlus,
  Video,
  FileText,
  MessageSquare,
  ShoppingBag,
  File,
  Image,
  Wrench,
  Hash,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  X,
  Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const getAuthToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('authToken');
  }
  return null;
};

// Content type icons and labels
const TYPE_CONFIG: Record<string, { icon: React.ElementType; labelKey: string; color: string }> = {
  video: { icon: Video, labelKey: 'components.collectionsSidebar.typeVideos', color: 'text-red-400' },
  article: { icon: FileText, labelKey: 'components.collectionsSidebar.typeArticles', color: 'text-blue-400' },
  social: { icon: MessageSquare, labelKey: 'components.collectionsSidebar.typeSocial', color: 'text-cyan-400' },
  product: { icon: ShoppingBag, labelKey: 'components.collectionsSidebar.typeProducts', color: 'text-amber-400' },
  document: { icon: File, labelKey: 'components.collectionsSidebar.typeDocuments', color: 'text-emerald-400' },
  image: { icon: Image, labelKey: 'components.collectionsSidebar.typeImages', color: 'text-pink-400' },
  tool: { icon: Wrench, labelKey: 'components.collectionsSidebar.typeTools', color: 'text-iris-400' },
  other: { icon: File, labelKey: 'components.collectionsSidebar.typeOther', color: 'text-gray-400' },
};

interface Collection {
  id: string;
  name: string;
  icon: string;
  color: string | null;
  position: number;
  isInbox: boolean;
  linkCount: number;
}

interface Tag {
  name: string;
  count: number;
}

interface TypeCount {
  type: string;
  count: number;
}

interface CollectionsSidebarProps {
  selectedCollection: string | null;
  selectedType: string | null;
  selectedTags: string[];
  onCollectionSelect: (id: string | null) => void;
  onTypeSelect: (type: string | null) => void;
  onTagSelect: (tag: string) => void;
  typeCounts?: TypeCount[];
}

export default function CollectionsSidebar({
  selectedCollection,
  selectedType,
  selectedTags,
  onCollectionSelect,
  onTypeSelect,
  onTagSelect,
  typeCounts = [],
}: CollectionsSidebarProps) {
  const { t } = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [collectionsExpanded, setCollectionsExpanded] = useState(true);
  const [typesExpanded, setTypesExpanded] = useState(true);
  const [tagsExpanded, setTagsExpanded] = useState(true);

  // Create collection modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionIcon, setNewCollectionIcon] = useState('📁');
  const [creating, setCreating] = useState(false);

  // Edit/delete state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch collections
  const fetchCollections = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      // Ensure inbox exists first
      await fetch(`${API_URL}/library/folders/ensure-inbox`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const res = await fetch(`${API_URL}/library/folders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setCollections(data.collections || []);
      }
    } catch (e) {
      console.error('Failed to fetch collections:', e);
    }
  }, []);

  // Fetch tags
  const fetchTags = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/library/folders/tags?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setTags(data.tags || []);
      }
    } catch (e) {
      console.error('Failed to fetch tags:', e);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCollections(), fetchTags()]).finally(() => setLoading(false));
  }, [fetchCollections, fetchTags]);

  // Create collection
  const handleCreate = async () => {
    if (!newCollectionName.trim()) return;

    const token = getAuthToken();
    if (!token) return;

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/library/folders`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newCollectionName.trim(),
          icon: newCollectionIcon,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setCollections(prev => [...prev, data.collection]);
        setShowCreateModal(false);
        setNewCollectionName('');
        setNewCollectionIcon('📁');
      }
    } catch (e) {
      console.error('Failed to create collection:', e);
    } finally {
      setCreating(false);
    }
  };

  // Rename collection
  const handleRename = async (id: string) => {
    if (!editName.trim()) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/library/folders/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: editName.trim() }),
      });

      if (res.ok) {
        setCollections(prev =>
          prev.map(c => (c.id === id ? { ...c, name: editName.trim() } : c))
        );
        setEditingId(null);
      }
    } catch (e) {
      console.error('Failed to rename collection:', e);
    }
  };

  // Delete collection
  const handleDelete = async (id: string) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/library/folders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setCollections(prev => prev.filter(c => c.id !== id));
        if (selectedCollection === id) {
          onCollectionSelect(null);
        }
      }
    } catch (e) {
      console.error('Failed to delete collection:', e);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  // Common emoji options
  const EMOJI_OPTIONS = ['📁', '📥', '💼', '🔬', '📚', '🎯', '💡', '🚀', '⭐', '❤️', '🔖', '📌'];

  if (loading) {
    return (
      <div className="w-56 shrink-0 p-4">
        <Loader2 className="w-5 h-5 text-[#9a9aac] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <aside className="w-56 shrink-0 border-r border-white/5 overflow-y-auto">
      <div className="p-4 space-y-6">
        {/* Collections Section */}
        <div>
          <button
            onClick={() => setCollectionsExpanded(!collectionsExpanded)}
            className="flex items-center justify-between w-full text-xs font-semibold text-[#9a9aac] uppercase tracking-wider mb-2 hover:text-white transition-colors"
          >
            <span>{t('components.collectionsSidebar.collections')}</span>
            {collectionsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {collectionsExpanded && (
            <div className="space-y-1">
              {/* All items option */}
              <button
                onClick={() => onCollectionSelect(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedCollection === null
                    ? 'bg-iris-500/20 text-iris-300'
                    : 'text-[#a0a0b0] hover:bg-white/5 hover:text-white'
                }`}
              >
                <Folder size={16} />
                <span className="flex-1 text-left truncate">{t('components.collectionsSidebar.all')}</span>
              </button>

              {collections.map(collection => (
                <div key={collection.id} className="relative group">
                  {editingId === collection.id ? (
                    <div className="flex items-center gap-1 px-2 py-1">
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename(collection.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="flex-1 px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white"
                        autoFocus
                      />
                      <button
                        onClick={() => handleRename(collection.id)}
                        className="p-1 text-emerald-400 hover:bg-white/10 rounded"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1 text-[#9a9aac] hover:bg-white/10 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => onCollectionSelect(collection.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                        selectedCollection === collection.id
                          ? 'bg-iris-500/20 text-iris-300'
                          : 'text-[#a0a0b0] hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {collection.isInbox ? (
                        <Inbox size={16} className="text-amber-400" />
                      ) : (
                        <span className="text-base leading-none">{collection.icon}</span>
                      )}
                      <span className="flex-1 text-left truncate">{collection.name}</span>
                      {collection.linkCount > 0 && (
                        <span className="text-xs text-[#9a9aac]">{collection.linkCount}</span>
                      )}
                    </button>
                  )}

                  {/* Menu button (not for Inbox) */}
                  {!collection.isInbox && editingId !== collection.id && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === collection.id ? null : collection.id);
                      }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 text-[#9a9aac] hover:text-white hover:bg-white/10 rounded transition-all"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  )}

                  {/* Dropdown menu */}
                  {menuOpenId === collection.id && (
                    <div className="absolute right-0 top-full mt-1 w-32 py-1 bg-[#16161e] border border-white/10 rounded-lg shadow-xl z-50">
                      <button
                        onClick={() => {
                          setEditingId(collection.id);
                          setEditName(collection.name);
                          setMenuOpenId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#a0a0b0] hover:bg-white/5 hover:text-white"
                      >
                        <Pencil size={14} />
                        {t('components.collectionsSidebar.rename')}
                      </button>
                      <button
                        onClick={() => {
                          setDeleteConfirmId(collection.id);
                          setMenuOpenId(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 size={14} />
                        {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* New collection button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-[#9a9aac] hover:bg-white/5 hover:text-white transition-colors"
              >
                <FolderPlus size={16} />
                <span>{t('components.collectionsSidebar.newCollection')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Types Section */}
        <div>
          <button
            onClick={() => setTypesExpanded(!typesExpanded)}
            className="flex items-center justify-between w-full text-xs font-semibold text-[#9a9aac] uppercase tracking-wider mb-2 hover:text-white transition-colors"
          >
            <span>{t('components.collectionsSidebar.types')}</span>
            {typesExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {typesExpanded && (
            <div className="space-y-1">
              <button
                onClick={() => onTypeSelect(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedType === null
                    ? 'bg-iris-500/20 text-iris-300'
                    : 'text-[#a0a0b0] hover:bg-white/5 hover:text-white'
                }`}
              >
                <File size={16} />
                <span className="flex-1 text-left">{t('components.collectionsSidebar.allTypes')}</span>
              </button>

              {Object.entries(TYPE_CONFIG).map(([type, config]) => {
                const count = typeCounts.find(t => t.type === type)?.count || 0;
                if (count === 0 && type !== 'article') return null;

                const Icon = config.icon;
                return (
                  <button
                    key={type}
                    onClick={() => onTypeSelect(type)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      selectedType === type
                        ? 'bg-iris-500/20 text-iris-300'
                        : 'text-[#a0a0b0] hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon size={16} className={config.color} />
                    <span className="flex-1 text-left">{t(config.labelKey)}</span>
                    {count > 0 && <span className="text-xs text-[#9a9aac]">{count}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Tags Section */}
        {tags.length > 0 && (
          <div>
            <button
              onClick={() => setTagsExpanded(!tagsExpanded)}
              className="flex items-center justify-between w-full text-xs font-semibold text-[#9a9aac] uppercase tracking-wider mb-2 hover:text-white transition-colors"
            >
              <span>{t('components.collectionsSidebar.tags')}</span>
              {tagsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>

            {tagsExpanded && (
              <div className="space-y-1">
                {tags.map(tag => (
                  <button
                    key={tag.name}
                    onClick={() => onTagSelect(tag.name)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      selectedTags.includes(tag.name)
                        ? 'bg-iris-500/20 text-iris-300'
                        : 'text-[#a0a0b0] hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Hash size={16} />
                    <span className="flex-1 text-left truncate">{tag.name}</span>
                    <span className="text-xs text-[#9a9aac]">{tag.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-80 p-4 bg-[#16161e] border border-white/10 rounded-xl shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">{t('components.collectionsSidebar.newCollection1')}</h3>

            <div className="space-y-4">
              {/* Icon picker */}
              <div>
                <label className="block text-sm text-[#9a9aac] mb-2">{t('components.collectionsSidebar.icon')}</label>
                <div className="flex flex-wrap gap-2">
                  {EMOJI_OPTIONS.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => setNewCollectionIcon(emoji)}
                      className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-colors ${
                        newCollectionIcon === emoji
                          ? 'bg-iris-500/30 ring-2 ring-iris-500'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name input */}
              <div>
                <label className="block text-sm text-[#9a9aac] mb-2">{t('components.collectionsSidebar.name')}</label>
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={e => setNewCollectionName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  placeholder={t('components.collectionsSidebar.collectionName')}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-[#9a9aac] focus:border-iris-500/50 focus:outline-hidden"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-[#9a9aac] hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={!newCollectionName.trim() || creating}
                className="px-4 py-2 bg-iris-500/20 text-iris-300 text-sm font-medium rounded-lg hover:bg-iris-500/30 disabled:opacity-50 transition-colors"
              >
                {creating ? <Loader2 size={16} className="animate-spin" /> : t('components.collectionsSidebar.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirmId(null)}>
          <div className="w-80 p-5 bg-[#16161e] border border-white/10 rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 size={18} className="text-red-400" />
              </div>
              <h3 className="text-sm font-semibold text-white">{t('components.collectionsSidebar.deleteCollection')}</h3>
            </div>
            <p className="text-sm text-[#a0a0b0] mb-5">
              {t('components.collectionsSidebar.deleteConfirmMessage')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm text-[#9a9aac] hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="px-4 py-2 bg-red-500/20 text-red-400 text-sm font-medium rounded-lg hover:bg-red-500/30 transition-colors"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
